import Mosaic from 'mosaic-framework';

import '../components/navigation-bar';
import '../components/footer';
import '../components/round-button';
import '../components/code-segment';

import '../styles/docs.less';


export default new Mosaic({
    name: 'data-page',
    created() {
        window.scrollTo({ top: 0 });

        const menubtn = document.getElementById('menu-button');
        menubtn.style.display = 'block';

        if(menubtn.onclick) return;

        menubtn.onclick = () => {
            const menu = document.getElementById('doc-menu');
            menu.style.transform = 'translateX(0%)';
        }
    },
    view() {
        return html`<div class='doc-page'>
            <navigation-bar></navigation-bar>
            
            <h1 class='section-title' tabindex='0'>⚡️ Observable Data</h1>
            <p class='section-text' tabindex='0'>
                Data represents the current state of a component. One of the biggest
                differences from other UI libraries is that 
                there is no difference between "state" and "props." Instead, everything
                is collected into one property called <b>data</b>. A component can define
                its own data by adding a "data" object to its constructor like so:
            </p>
            <span class='section-text'><i style='color:gray;margin-bottom:0px'>index.js</i></span>
            <code-segment type='JS'>
                new Mosaic({
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;name: 'my-component',
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;data: {
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                count: 0
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;}
                <br>
                });
            </code-segment>

            <p class='section-text' tabindex='0'>
                It is also possible to pass data down to a child component. This is done by 
                setting HTML attributes on the component tag the same way you would set
                something like "class" or "style." For an attribute to count as data, however,
                it must first have a default value specifed to indicate the it is going to be
                injected later on. In this example, "title" will count as a regular HTML attribute
                because it is not included in the data property of our component. "Count,"
                however, will be included as data because we specifed a default value for it when
                we created the component.
            </p>
            <code-segment>
                &lt;my-component title='This will count as a regular HTML attribute' count='This will count as data!'&gt;
                <br>
                &lt;/my-component&gt;
            </code-segment>
            <br>
            
            <h1 class='section-title' tabindex='0'>What is an Observable?</h1>
            <p class='section-text' tabindex='0'>
                An Observable is a data type that tracks changes to an object and performs
                an action right before and right after the object changes. The use of an
                observable allows Mosaic data to be changed directly in order to trigger a
                component update. Even though we define "data" as a regular JavaScript object,
                it will be turned into an Observable upon initialization. Printing it to the
                console will reveal it as a Proxy object, which is basically what an Observable
                is under the hood.
            </p>
            <p class='section-text' tabindex='0'>
                A component's data can be changed in two ways. The first is to directly set it
                using a single line like so:
            </p>
            <code-segment type='JS'>this.data.count += 10;</code-segment>
            <p class='section-text' tabindex='0'>
                Every time you change the data with a line such as this you will trigger the
                <b>repaint cycle</b> for that component. The repaint cycle will be further
                explained below, but for now just know that it is the process of applying DOM
                changes based on the component's data.
            </p>
            <p class='section-text' tabindex='0'>
                Array functions are also observable, so you can directly say "array.push" or
                "array.splice," or even change a value directly by saying "array[2] = ...".
                While this is possible, though, it is important to remember that it can be
                slow. Based on the way the renderer is currently set up, using observable
                array functions can be a very slow way to update a list when it starts to get
                very big. When you have very long lists of data to render, it is probably a
                better idea to change the array entirely by writing something like 
                "array = array.filter(...)" or "array = array.concat(...)".
            </p>
            <p class='section-text' tabindex='0'>
                What you will notice is that multiple calls to change data will result in many calls
                to begin the repaint cycle. Take a look at the example below where we update several
                data properties within the same scope. This will end up going through the repainting
                process five times in a row, making for a poor app performance:
            </p>
            <b style='color:crimson'>
                <ion-icon name="close"></ion-icon>
                &nbsp;Bad: Each data change will trigger a separate repaint process.
            </b>
            <code-segment type='JS'>
                this.data.age = 21;
                <br>
                this.data.name = "Bob";
                <br>
                this.data.location = "New York City";
                <br>
                this.data.favoriteFood = "Pasta";
                <br>
                this.data.school = {
                    name: "New York University",
                    year: "Senior"
                };
                <br>
            </code-segment>
            <br>
            <h1 class='section-title' tabindex='0'>
                Set(data: <span style="color:mediumseagreen">Object</span>)
            </h1>
            <p class='section-text' tabindex='0'>
                To solve the problem above, we can use the <b>set</b> function to update
                multiple data properties at once and only trigger a single repaint. This
                is extremely useful when you are making many data changes within the
                same scope and do not wish to make so many DOM updates.
            </p>
            <b style='color:mediumseagreen'>
                <ion-icon name="checkmark"></ion-icon>
                &nbsp;Good: The <i>set</i> function results in just one repaint call.
            </b>
            <code-segment type='JS'>
                this.set({
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                age: 21,
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                name: "Bob",
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                location: "New York City",
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                favoriteFood: "Pasta",
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                school: {
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                name: "New York University",
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                year: "Senior"
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                }
                <br>
                });
            </code-segment>
            <br>

            <h1 class='section-title' tabindex='0'>Repainting Process</h1>
            <p class='section-text' tabindex='0'>
                Now we will get into exactly what the repaint process is and how
                it applies DOM updates based on data changes. 
            </p>
            <p class='section-text' tabindex='0'>
                Mosaic uses what is known as a <b>Smart DOM</b> to update components.
                What this means is that component views will be memorized upon 
                initialization so that the location of dynamic content can be remembered 
                for later. This is where we begin the "repainting" process. Instead of 
                traversing the entire component again, Mosaic will simply loop through
                the memorized portions of its template and make DOM changes to the values
                that truly changed between repaints.
            </p>
            <p class='section-text' tabindex='0'>
                The use of memorized dynamic content is made possible through the use of
                <b>Tagged Template Literals</b>, which are a built-in feature of the
                JavaScript language. Tagged Template Literals will be discussed more when
                we talk about the <b>view</b> function. As of now, just know that a
                component's view will always reflect its current state as defined in the
                data property.
            </p>
            <p class='section-text' tabindex='0'>
                As Mosaic loops through your dynamic content, it will compare the old
                value with the new value at that content location and determine whether
                or not it actually changed. If you say "this.data.count = 5" when "count"
                is already 5, then no DOM changes will be applied. If you do, however, say
                "this.data.count = 10" then the Mosaic renderer will see that the value has
                actually changed and will proceed to make the appropriate DOM update.
            </p>
            <p class='section-text' tabindex='0'>
                The use of the Smart DOM already improves the performance of our web apps
                by not traversing the entire DOM tree multiple times. We can, however,
                improve performance even further by knowing that only dynamic content will
                be traversed again. If we aim to limit the number of times we use the "$ {}"
                syntax in our views, we can limit the number of places the renderer needs to
                look through when applying changes.
            </p>

            <h1 class='section-title' tabindex='0'>Repaint()</h1>
            <p class='section-text' tabindex='0'>
                Sometimes you will want to use dynamic values that are not included in your
                component's data property. These values will therefore not have their changes
                tracked like an observable would. So how can you use those values and still
                have your component update along with them? The answer is simple: you use the
                <b>repaint</b> function. This is a function that exists on all Mosaics, and
                while it is used more for internal updates with the library, developers are
                also free to use it whenever they want to trigger the repaint process again.
                You can think of it as being similar to React's "forceUpdate" function.
            </p>
            <code-segment type='JS'>
                nonObservableValue = "something else";
                <br>
                this.repaint();
            </code-segment>
            <br>

            <h1 class='section-title' tabindex='0'>Demo</h1>
            <p class='section-text' tabindex='0'>
                To finish off this section we will look at an interactive example of the
                <b>data</b> property. You can head over to JSFiddle to play around the
                example for yourself, as well.
            </p>
            <iframe width="100%" height="300" src="//jsfiddle.net/authman2/79pug4k3/51/embedded/js,html,result/" allowfullscreen="allowfullscreen" allowpaymentrequest frameborder="0"></iframe>
            <br>

            <p class='section-text' tabindex='0' style='text-align:center;'>
                Head over to the next section where we will discuss actions that you
                can add to your Mosaics!
            </p>
            <div style="text-align: center;">
                <round-button onclick='${() => this.router.send('/lifecycle')}'>
                    <ion-icon name="arrow-round-back"></ion-icon>
                    LIfecycle Hooks
                </round-button>
                &nbsp;&nbsp;
                <round-button onclick='${() => this.router.send('/actions')}'>
                    Actions and UDPs
                    <ion-icon name="arrow-round-forward"></ion-icon>
                </round-button>
            </div>

            <app-footer></app-footer>
        </div>`
    }
});